import React, { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Spin, message, Typography,
  Card, Row, Col, Statistic, Empty, Popconfirm,
  Modal, Form, Input, Space, Divider, Progress, Alert,
} from 'antd';
import {
  PlusOutlined, DollarOutlined, DeleteOutlined,
  CheckCircleOutlined, WalletOutlined, BankOutlined,
  ClockCircleOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import { fetchSettlementPayments, createSettlementPayment, deleteSettlementPayment, createSettlementPaymentVoucher } from '../api';
import ApprovalStatusBadge, { PendingApprovalAlert } from '../../../components/ApprovalStatusBadge';
import { roundAmount } from '../../../utils/goldCalculation';
import PaymentSplitInput, { DEFAULT_PAYMENT_MODES } from '../../../components/PaymentSplitInput';
import { SplitReferenceCell, resolveSplits } from '../../../components/PaymentSplitCells';
import dayjs from 'dayjs';

// Settlement payouts to customers are Cash or Bank Transfer only (no UPI / Cheque).
const SETTLEMENT_PAYMENT_MODES = DEFAULT_PAYMENT_MODES.filter(m => ['CASH', 'BANK_TRANSFER'].includes(m.value));

const { Text } = Typography;
const { TextArea } = Input;

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 2,
  }).format(val || 0);

const formatWholeAmount = (price) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(price || 0);


const SettlementPaymentTable = ({
  caseId,
  caseData,
  caseModel = 'PledgeReleaseCase',
  onPaymentAdded,
}) => {
  const [messageApi, contextHolder] = message.useMessage();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form] = Form.useForm();

  const loadPayments = async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const result = await fetchSettlementPayments(caseId);
      setPayments(result.data || []);
    } catch (error) {
      console.error('Load settlement payments error:', error);
      messageApi.error('Failed to load settlement payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, [caseId]);

  const c = caseData || {};
  const settlementAmount = caseModel === 'WalkInPurchaseCase'
    ? (c.finalPaymentToCustomer || 0)
    : (c.finalSettlementAmount || 0);
  const totalPaid = c.totalSettlementPaid || 0;
  // Derived, not read from remainingSettlementBalance: that field is a cache
  // and cases priced before payouts moved ahead of billing still carry its
  // default of 0, which would read as "fully paid" and lock this table.
  const remaining = parseFloat((settlementAmount - totalPaid).toFixed(2));
  const paidPercent = settlementAmount > 0 ? Math.min(100, (totalPaid / settlementAmount) * 100) : 0;
  const isFullyPaid = settlementAmount > 0 && remaining <= 0.5;

  const hasPendingApproval = payments.some(p => p.status === 'PENDING_TREASURY');

  const handleCreate = async (values) => {
    setCreating(true);
    try {
      const { paymentSplits, remarks, paymentDate } = values;
      const totalAmount = roundAmount((paymentSplits || []).reduce((s, p) => s + (Number(p?.amount) || 0), 0));
      const paymentResponse = await createSettlementPayment({
        caseId: c._id,
        caseModel,
        amount: totalAmount,
        paymentSplits,
        remarks,
        paymentDate,
      });

      const isPending = paymentResponse?.pendingTreasuryApproval || paymentResponse?.data?.status === 'PENDING_TREASURY';

      if (isPending) {
        const ref = paymentResponse?.treasuryReference || '';
        messageApi.info(`Payment submitted for approval${ref ? ` (Ref: ${ref})` : ''}. Check Workflow → My Approvals.`);
      } else {
        messageApi.success('Settlement payment recorded');
        // Auto-generate voucher only for immediately completed payments
        if (paymentResponse?.data?.paymentId) {
          try {
            await createSettlementPaymentVoucher(paymentResponse.data.paymentId);
            messageApi.success('Voucher generated automatically');
          } catch {
            messageApi.warning('Payment recorded, but voucher generation failed');
          }
        }
      }

      form.resetFields();
      setModalOpen(false);
      await loadPayments();
      onPaymentAdded?.();
    } catch (error) {
      console.error('Create settlement payment error:', error);
      messageApi.error(error?.response?.data?.message || 'Failed to create payment');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (paymentId, status) => {
    setDeletingId(paymentId);
    try {
      const res = await deleteSettlementPayment(paymentId);
      if (status === 'PENDING_TREASURY') {
        messageApi.success('Pending payment cancelled');
      } else {
        const dest = res?.data?.reversedTo === 'BANK' ? 'bank' : 'branch cash';
        messageApi.success(`Settlement payment reversed — funds returned to ${dest}`);
      }
      await loadPayments();
      onPaymentAdded?.();
    } catch (error) {
      console.error('Reverse settlement payment error:', error);
      messageApi.error(error?.response?.data?.message || 'Failed to reverse payment');
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date) => date ? dayjs(date).format('DD MMM YYYY') : '-',
      width: 110,
      sorter: (a, b) => new Date(a.paymentDate) - new Date(b.paymentDate),
    },
    {
      title: 'Payment ID',
      dataIndex: 'paymentId',
      key: 'paymentId',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Status',
      key: 'status',
      width: 180,
      render: (_, record) => (
        <ApprovalStatusBadge status={record.status} workflowInstance={record.workflowInstance} />
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => {
        const isVoid = record.status === 'REJECTED' || record.status === 'REVERSED';
        return (
          <Text
            strong
            delete={record.status === 'REVERSED'}
            style={{ color: isVoid ? '#8c8c8c' : '#3f8600' }}
          >
            {formatWholeAmount(amount)}
          </Text>
        );
      },
      width: 130,
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: 'Approval Ref',
      dataIndex: ['treasuryRequestId', 'reference'],
      key: 'treasuryRef',
      width: 120,
      ellipsis: true,
      render: (ref) => ref ? <Text type="secondary" style={{ fontSize: 11 }}>{ref}</Text> : '-',
    },
    {
      title: 'UTR / Ref.',
      key: 'transactionReference',
      width: 240,
      render: (_, record) => (
        <SplitReferenceCell record={record} splits={resolveSplits(record)} expectedTotal={record.amount} />
      ),
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      ellipsis: true,
      width: 180,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 110,
      render: (_, record) => {
        const isPending = record.status === 'PENDING_TREASURY';
        // Terminal states — nothing left to reverse or cancel.
        if (record.status === 'REVERSED' || record.status === 'REJECTED') {
          return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        }
        return (
          <Popconfirm
            title={isPending ? 'Cancel pending payment?' : 'Reverse this settlement payment?'}
            description={isPending
              ? 'This will cancel the pending approval request.'
              : 'Funds return to their source (branch cash or bank), the case balance is rolled back, and the ledger entry is reversed. The payment record is kept.'}
            okText={isPending ? 'Cancel Payment' : 'Reverse'}
            okButtonProps={{ danger: true, loading: deletingId === record.paymentId }}
            onConfirm={() => handleDelete(record.paymentId, record.status)}
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deletingId === record.paymentId}
            >
              {isPending ? 'Cancel' : 'Reverse'}
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <>
      {contextHolder}

      {/* Pending approval alert — dynamic approver info */}
      {hasPendingApproval && <PendingApprovalAlert payments={payments} />}

      {/* Summary Cards */}
      <Row gutter={[16, 16]} className="mb-3">
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered style={{ background: '#f0f5ff' }}>
            <Statistic
              title="Net Settlement Amount"
              value={settlementAmount}
              precision={0}
              prefix={<BankOutlined />}
              suffix="₹"
              valueStyle={{ fontSize: 16, color: '#1d39c4' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered>
            <Statistic
              title="Total Paid to Customer"
              value={totalPaid}
              precision={0}
              prefix={<DollarOutlined />}
              suffix="₹"
              valueStyle={{ fontSize: 16, color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered style={{ background: remaining > 0.5 ? '#fffbe6' : '#f6ffed' }}>
            <Statistic
              title="Remaining to Pay"
              value={Math.max(0, remaining)}
              precision={0}
              prefix={remaining > 0.5 ? <WalletOutlined /> : <CheckCircleOutlined />}
              suffix="₹"
              valueStyle={{ fontSize: 16, color: remaining > 0.5 ? '#fa8c16' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" bordered>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Payment Progress</Text>
            </div>
            <Progress
              percent={parseFloat(paidPercent.toFixed(1))}
              status={isFullyPaid ? 'success' : 'active'}
              strokeColor={isFullyPaid ? '#52c41a' : '#1890ff'}
            />
          </Card>
        </Col>
      </Row>

      {/* Add Payment Button */}
      <Row justify="space-between" align="middle" className="mb-3">
        <Col>
          <Text strong style={{ fontSize: 15 }}>
            Settlement Payments to Customer
            {payments.length > 0 && <Text type="secondary"> ({payments.length})</Text>}
          </Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              form.setFieldsValue({
                paymentSplits: [{ mode: 'BANK_TRANSFER', amount: Math.max(0, remaining), reference: '' }],
              });
              setModalOpen(true);
            }}
            disabled={isFullyPaid || hasPendingApproval}
          >
            Add Settlement Payment
          </Button>
          {isFullyPaid && (
            <Tag color="green" style={{ marginLeft: 12, fontSize: 13, padding: '4px 12px' }}>
              <CheckCircleOutlined /> Fully Paid
            </Tag>
          )}
          {hasPendingApproval && !isFullyPaid && (
            <Tag color="orange" style={{ marginLeft: 12, fontSize: 13, padding: '4px 12px' }}>
              <ClockCircleOutlined /> Pending Approval
            </Tag>
          )}
        </Col>
      </Row>

      {/* Payments Table */}
      <Spin spinning={loading}>
        <Table
          dataSource={payments.map((p) => ({ ...p, key: p._id }))}
          columns={columns}
          pagination={payments.length > 10 ? { pageSize: 10, showSizeChanger: true } : false}
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
          rowClassName={(record) => record.status === 'PENDING_TREASURY' ? 'ant-table-row-warning' : ''}
          locale={{
            emptyText: (
              <Empty
                description="No settlement payments recorded yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Spin>

      {/* Create Payment Modal */}
      <Modal
        title={
          <Space>
            <DollarOutlined />
            Record Settlement Payment to Customer
          </Space>
        }
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Divider style={{ margin: '12px 0' }} />

        {/* Every payment now routes through an approval workflow — nothing auto-completes. */}
        {remaining > 0 && (
          <Alert
            type="warning"
            showIcon
            message="Requires Approval"
            description="Every settlement payment goes through an approval workflow before disbursement. Enter the modes and amounts only — for bank transfers the approver records the UTR when they make the payment. Bank payments deduct from the Central Fund; cash payments deduct from the branch cash (funded via IBCT) — both on approval."
            style={{ marginBottom: 12 }}
          />
        )}

        <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Text type="secondary">Settlement Amount</Text>
              <br />
              <Text strong>{formatCurrency(settlementAmount)}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Remaining</Text>
              <br />
              <Text strong style={{ color: remaining > 0.5 ? '#fa8c16' : '#3f8600' }}>
                {formatCurrency(Math.max(0, remaining))}
              </Text>
            </Col>
          </Row>
        </Card>

        <Form form={form} layout="vertical" onFinish={handleCreate}>
          {/* Bank rows carry no reference here: the UTR does not exist until the
              transfer is actually made, which happens when the approver approves.
              They enter it against each split on the Approve action. */}
          <PaymentSplitInput
            fieldName="paymentSplits"
            maxAmount={Math.max(0, remaining)}
            paymentModes={SETTLEMENT_PAYMENT_MODES}
            referenceModes={['CASH']}
            referenceHint="UTR entered by approver"
          />

          <Divider style={{ margin: '12px 0' }} />

          <Form.Item name="remarks" label="Remarks">
            <TextArea rows={2} placeholder="Optional notes" />
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const splits = getFieldValue('paymentSplits') || [];
              const hasBank = splits.some(s => ['BANK_TRANSFER', 'UPI', 'CHEQUE'].includes(s?.mode));
              // Bank/UPI/cheque always needs approval. Pledge cash may need approval if it
              // exceeds the pledge limit — the server decides, so use a neutral label for pledge cash.
              const needsApproval = hasBank;
              const submitLabel = needsApproval
                ? 'Submit for Approval'
                : (caseModel === 'PledgeReleaseCase' ? 'Submit Payment' : 'Record Payment');
              return (
                <Row justify="end">
                  <Space>
                    <Button onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<DollarOutlined />}
                      loading={creating}
                    >
                      {submitLabel}
                    </Button>
                  </Space>
                </Row>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SettlementPaymentTable;
